/** Viewport dimensions */
import { useSyncExternalStore } from 'react'

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl'

const useMediaQuery = (query: string): boolean => {
  const subscribe = (callback: () => void) => {
    const mql = window.matchMedia(query)
    mql.addEventListener('change', callback)
    return () => mql.removeEventListener('change', callback)
  }
  const getSnapshot = () => window.matchMedia(query).matches
  return useSyncExternalStore(subscribe, getSnapshot, () => false)
}

/** Returns dimensions. Note that isSmall and isMedium  */
export const useDimensions = () => {
  const isXs = useMediaQuery('(max-width:599.95px)')
  const isSm = useMediaQuery('(min-width:600px) and (max-width:899.95px)')
  const isMd = useMediaQuery('(min-width:900px) and (max-width:1199.95px)')
  const isLg = useMediaQuery('(min-width:1200px) and (max-width:1535.95px)')
  const isXl = useMediaQuery('(min-width:1536px)')

  const orientation = screen.orientation?.type ?? 'portrait-primary'

  const isPortrait = orientation === 'portrait-primary' || 'portrait-secondary'
  const isLandscape = orientation === 'landscape-primary' || 'landscape-secondary'

  const isTablet = isMd || isSm
  const isDesktop = isLg || isXl

  let size: Breakpoint
  if (isXs) {
    size = 'xs'
  } else if (isSm) {
    size = 'sm'
  } else if (isMd) {
    size = 'md'
  } else if (isLg) {
    size = 'lg'
  } else {
    size = 'xl'
  }

  return {
    size,
    isTiny: isXs && !isSm,
    isSmallOrSmaller: isXs || isSm,
    isSmall: isSm,
    isMediumOrSmaller: isMd || isSm || isXs,
    isMedium: isMd,
    isLargeOrSmaller: isLg || isMd || isSm || isXs,
    isLarge: isLg,
    isXLargeOrSmaller: isXl || isLg || isMd || isSm || isXs,
    isXLarge: isXl,
    isPortrait,
    isLandscape,
    orientation,
    isDesktop,
    isTablet,
  }
}
